'use client'

import { ROUTES } from '@/core/constants/routes'
import { Button } from '@/core/ui/atoms/button'
import { FormInput } from '@/core/ui/forms/input'
import { Link, usePathname } from '@/i18n/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { FormProvider } from 'react-hook-form'
import { useLogin } from './use-login'
import { cn } from '@/core/lib/utils/cn'

type Props = {
  onSuccess: () => void
}

export const LoginForm: React.FC<Props> = props => {
  const t = useTranslations()
  const { form, handleSubmit, isPending } = useLogin(props)
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const locale = useLocale()
  const getCreateAccountLink = () => {
    const params = new URLSearchParams(searchParams)
    params.set('auth', 'register')
    return `${pathname}?${params.toString()}`
  }

  const getForgotPasswordLink = () => {
    const params = new URLSearchParams(searchParams)
    params.set('auth', 'forgot-password')
    return `${pathname}?${params.toString()}`
  }
  return (
    <div className='px-1 py-3 sm:px-10 sm:py-5'>
      <div className='mb-3 flex items-start gap-2'>
        <h1 className='text-mountain-bluebird text-xl font-semibold sm:text-[28px]'>
          {t('signin.title')}
        </h1>
      </div>

      <p className='text-mountain-bluebird mb-5 text-base sm:text-lg'>
        {t('signin.subtitle')}
      </p>

      <FormProvider {...form}>
        <form onSubmit={handleSubmit} className='space-y-4'>
          <FormInput
            name='email'
            label={t('forms.emailLabel')}
            placeholder={t('forms.emailPH')}
            control={form.control}
          />

          <FormInput
            name='password'
            label={t('forms.passwordLabel')}
            placeholder={t('forms.passwordPH')}
            type='password'
            control={form.control}
          />

          <Link
            href={getForgotPasswordLink()}
            className='text-cyan-blue text-sm font-bold underline sm:text-base'
          >
            {t('actions.resetPassword')}
          </Link>

          <Button
            loading={isPending}
            color='creamed-cron'
            className='mt-5 h-12 w-full font-semibold text-white sm:py-3'
          >
            {t('actions.continue')}
          </Button>

          <p className='text-mountain-bluebird mt-3 text-sm leading-snug'>
            {t('acceptTerms.accept')}{' '}
            <Link href={ROUTES.terms} className='text-creamed-cron underline'>
              {t('terms.title')}
            </Link>{' '}
            {t('conjunctions.and')}{' '}
            <Link href={ROUTES.privacy} className='text-creamed-cron underline'>
              {t('privacy.title')}
            </Link>
            .
          </p>

          <p
            className={cn(
              'mt-3 flex text-base text-gray-700',
              locale === 'ar' ? 'flex-row-reverse justify-center' : 'flex-row',
            )}
          >
            {t('signin.new')}{' '}
            <Link
              href={getCreateAccountLink()}
              className='text-creamed-cron font-medium underline'
            >
              {t('actions.createAccount')}
            </Link>
          </p>
        </form>
      </FormProvider>
    </div>
  )
}
